import { Icon, Spinner } from "dash-ui";
import {
  GetBrokerPropertiesLocationFilterInput,
  Property,
  useGetIdxEmbeddedSearchBarFeedQuery,
  useGetIdxSearchFeedPropertiesQuery,
} from "gql/generated.graphql";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FiChevronLeft,
  FiChevronRight,
  FiGrid,
  FiLoader,
  FiMapPin,
  FiSearch,
  FiRefreshCcw,
} from "react-icons/fi";
import { Link, useParams, useLocation } from "react-router-dom";
import { useImmer } from "use-immer";
import DashLogo from "assets/images/png/SignInLogo.png";
import { lighten } from "polished";
import { ExchangeMapView } from "v2/components/idx/exchange-map-view";
import { SearchBarFilters } from "v2/components/idx/components/search-bar/search-bar-filters";
import { IdxEmbeddedSearchPageFilters } from "v2/components/idx/constants/constants";
import FilterDrawer from "v2/components/idx/components/search-bar/filter-drawer";
import { DEFAULT_PROPERTIES_PER_PAGE } from "v2/components/idx/constants/constants";
import { useRestCall } from "common/hooks/useRestCall/useRestCall";
import { PropertyTransactionTypesEnum } from "dash-common";

type SelectedView = "GRID" | "MAP";

// TODO: [ ] Implement location filter on map view

// Initial filter values
const initialFilters: IdxEmbeddedSearchPageFilters = {
  buildingTypes: [],
  marketAreaIds: [],
  priceRange: { min: 0, max: 0 },
  projectStatus: [],
  transactionType: [PropertyTransactionTypesEnum.FOR_SALE],
  searchText: "",
  locationFilter: {
    lat: 0,
    lng: 0,
  },
};

type LatLng = { lat: number; lng: number };

export type IdxPreviousPageState = {
  type: SelectedView;
  mapState?: {
    position: LatLng;
    zoomLevel: number;
  };
  filters?: IdxEmbeddedSearchPageFilters;
  pagination: {
    limit: number;
    selectedPage: number;
  };
};

export function IdxEmbeddedSearchPage() {
  const location = useLocation();

  // The state of the search page before clicking on a property
  const [previousState, setPreviousState] =
    useState<IdxPreviousPageState | null>(location?.state);
  // The previous state of the grid view
  const previousGridPagination = previousState?.pagination;
  // The previous state of the map view
  const previousMapState = previousState?.mapState;

  const previousStateFilters = previousState?.filters;

  const previousSelectedPage = previousGridPagination?.selectedPage;
  const previousSelectedLimit = previousGridPagination?.limit;
  const previousSelectedView = previousState?.type;

  const [selectedView, setSelectedView] = useState<SelectedView>(
    previousSelectedView ?? "GRID"
  );

  const [limit, setLimit] = useState<number>(
    previousSelectedLimit ?? DEFAULT_PROPERTIES_PER_PAGE
  );
  const [skip, setSkip] = useState<number>(
    previousSelectedPage ? (previousSelectedPage - 1) * limit : 0
  );
  const [searchInputValue, setSearchInputValue] = useState<string>(
    previousStateFilters?.searchText || ""
  );
  const searchDebounce = useRef<NodeJS.Timeout | undefined>();
  // Params
  const params = useParams();
  const idxFeedId = params.idxFeedId;

  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // Filter State
  const [filters, setFilters] = useImmer<IdxEmbeddedSearchPageFilters>(
    previousStateFilters || initialFilters
  );
  const isFilterAltered =
    JSON.stringify(filters) !==
    JSON.stringify(previousStateFilters || initialFilters);

  const [isMapAreaRestricted, setIsMapAreaRestricted] = useState(
    previousSelectedView === "MAP" ? true : false
  );

  const didUserInteractWithForm = useRef(false);

  // Get idx settings and feed settings query
  const { data: getIdxData, loading: loadingSettings } =
    useGetIdxEmbeddedSearchBarFeedQuery({
      variables: {
        input: {
          idxFeedId,
        },
      },
      fetchPolicy: "cache-first",
      onCompleted: (data) => {
        const initialView =
          data.getIdxEmbeddedSearchBarFeed.idxFeed.searchBarContent ===
          "LIST_IN_MAP"
            ? "MAP"
            : "GRID";
        setSelectedView(previousSelectedView ?? initialView);
      },
    });

  // Get properties query
  const gridProperties = useGetIdxSearchFeedPropertiesQuery({
    variables: {
      input: {
        idxFeedId,
        filters: {
          ...filters,
          transactionType: filters.transactionType[0],
        },
        pagination: {
          skip,
          limit,
        },
      },
    },
    skip:
      selectedView === "MAP" ||
      !getIdxData?.getIdxEmbeddedSearchBarFeed.idxFeed, // only get properties after settings were retrieved.
    fetchPolicy: "cache-first",
  });

  const mapProperties = useRestCall({
    endpoint: `idx/embedded/${idxFeedId}/properties`,
    method: "POST",
    body: {
      input: {
        idxFeedId,
        filters: {
          ...filters,
          transactionType: filters.transactionType[0],
        },
        pagination: {
          limit: 1000,
          skip: 0,
        },
      },
    },
    dataWrapper: "getIdxSearchFeedProperties",
    skip: selectedView !== "MAP",
  });

  const { data: getPropertiesData, loading: loadingProperties } =
    selectedView === "GRID" ? gridProperties : mapProperties;

  const idxFeed = getIdxData?.getIdxEmbeddedSearchBarFeed.idxFeed;
  const idxSettings = getIdxData?.getIdxEmbeddedSearchBarFeed?.idxSettings;
  const searchbarContent =
    getIdxData?.getIdxEmbeddedSearchBarFeed.idxFeed.searchBarContent;
  const properties =
    getPropertiesData?.getIdxSearchFeedProperties.properties.collection || [];
  const propertiesTotal =
    getPropertiesData?.getIdxSearchFeedProperties.properties.totalCount || 0;
  const shouldRenderContent =
    searchbarContent === "SEARCH_BAR_ONLY"
      ? didUserInteractWithForm.current
      : true;

  // Filter actions
  async function addSearchTextFilter(value: string) {
    setSearchInputValue(value);
    if (searchDebounce.current) {
      clearTimeout(searchDebounce.current);
    }
    searchDebounce.current = setTimeout(() => {
      setFilters((draft) => {
        draft.searchText = value;
      });
    }, 1000);
  }
  async function addLocationFilter(
    value: GetBrokerPropertiesLocationFilterInput
  ) {
    setFilters((draft) => {
      draft.locationFilter.bounds = value.bounds;
      draft.locationFilter.lat = value.lat;
      draft.locationFilter.lng = value.lng;
    });
  }

  const clearLocationFilter = async () => {
    setFilters((draft) => {
      draft.locationFilter.bounds = null;
      draft.locationFilter.lat = 0;
      draft.locationFilter.lng = 0;
    });
  };

  const hasActiveFilters = useMemo(() => {
    const isBuildingTypesFilterActive = filters.buildingTypes.length > 0;
    const isMarketAreaFilterActive = filters.marketAreaIds.length > 0;
    const isPriceRangeFilterActive =
      (filters?.priceRange?.min && filters.priceRange.min > 0) ||
      (filters?.priceRange?.max && filters.priceRange.max > 0);
    const isProjectStatusFilterActive = filters.projectStatus.length > 0;

    if (
      isBuildingTypesFilterActive ||
      isMarketAreaFilterActive ||
      isPriceRangeFilterActive ||
      isProjectStatusFilterActive
    ) {
      return true;
    }
    return false;
  }, [filters]);

  // Derived states for pagination
  const lastPage = Math.ceil(
    (propertiesTotal || idxFeed?.propertiesPerPage || 1) /
      (idxFeed?.propertiesPerPage || 1)
  );
  const allPages =
    new Array(lastPage || 1).fill(null).map((_, index) => index + 1) || [];
  const selectedPage = skip / (idxFeed?.propertiesPerPage || 1) + 1;
  const previousPages = allPages.slice(0, selectedPage - 1);
  const nextPages = allPages.slice(selectedPage, allPages.length);
  const filteredPreviousPages =
    nextPages.length < 3
      ? previousPages.slice(-6 + nextPages.length)
      : previousPages.slice(-3);
  const filteredNextPages =
    previousPages.length < 3
      ? nextPages.slice(0, 6 - previousPages.length)
      : nextPages.slice(0, 3);
  const pages = [...filteredPreviousPages, selectedPage, ...filteredNextPages];

  // Pagination functions
  const onNextPage = useCallback(() => {
    if (selectedPage === lastPage) return;
    if (!idxFeed?.propertiesPerPage) return;
    setSkip((currentValue) => currentValue + (idxFeed.propertiesPerPage || 0));
  }, [selectedPage, lastPage, idxFeed]);
  const onPreviousPage = useCallback(() => {
    if (selectedPage === 1) return;
    if (!idxFeed?.propertiesPerPage) return;
    setSkip((currentValue) => currentValue - (idxFeed.propertiesPerPage || 0));
  }, [selectedPage, idxFeed]);
  const onGoToPage = useCallback(
    (page: number) => {
      if (!idxFeed?.propertiesPerPage) return;
      setSkip(page * idxFeed?.propertiesPerPage - idxFeed?.propertiesPerPage);
    },
    [idxFeed]
  );

  // Use effect to load all properties on the map while implementing location filter
  useEffect(() => {
    if (idxFeed) {
      if (selectedView === "MAP") {
        setLimit(400);
        setSkip(0);
      }
      if (selectedView === "GRID") {
        setLimit(idxFeed.propertiesPerPage || 0);
      }
    }
  }, [idxFeed, selectedView]);

  // Effect to detect the first user interaction with the search bar.
  useEffect(() => {
    if (didUserInteractWithForm.current) return;
    if (!!searchInputValue || isFilterAltered)
      didUserInteractWithForm.current = true;
  }, [searchInputValue, filters]);

  // Branding Colors
  const mainColor = idxSettings?.branding.mainColor || "#1B75BB";
  const mainColorLight = lighten(0.4, mainColor);
  const textColor = idxSettings?.branding.textColor || "#1e293b";
  const textColorLight = lighten(0.4, textColor);
  const backgroundColor = idxSettings?.branding.backgroundColor || "";

  const resolveGridPropertyCountText = () => {
    const startPosition = propertiesTotal === 0 ? 0 : skip + 1;
    const endPosition = skip + (properties.length || skip);
    const propertyText =
      propertiesTotal > 1 || propertiesTotal === 0 ? "Properties" : "Property";

    return `Showing ${startPosition} - ${endPosition} of ${propertiesTotal} ${propertyText}`;
  };

  const handleSearchInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    addSearchTextFilter(event.target.value);
    setSkip(0);
  };

  const renderFilterButton = () => {
    return (
      <div className="relative gap-2 items-center flex min-[840px]:hidden flex-wrap justify-center">
        <button
          className={`p-2 px-4 rounded-full border hover:bg-gray-50`}
          style={{ borderColor: mainColor }}
          onClick={() => setIsFilterOpen(true)}
        >
          <Icon name="setting2" size={20} color={mainColor} />
        </button>
        {isFilterOpen && (
          <FilterDrawer
            onClose={() => setIsFilterOpen(false)}
            filters={filters}
            mainColor={mainColor}
            mainColorLight={mainColorLight}
            textColor={textColor}
            setFilters={setFilters}
            goToFirstPage={() => setSkip(0)}
          />
        )}
        {hasActiveFilters && (
          <div
            className="flex bg-white rounded-full absolute justify-center items-center"
            style={{ width: 18, height: 18, top: -4, right: -2 }}
          >
            <div
              className="rounded-full w-3 h-3"
              style={{ backgroundColor: mainColor }}
            ></div>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Loading Feed Settings */}
      {loadingSettings && selectedView === "GRID" && <Spinner fullContent />}

      {!loadingSettings && (
        <>
          <div
            className="flex flex-col items-center"
            style={{ backgroundColor: backgroundColor, color: textColor }}
          >
            <div className="flex flex-col w-full max-w-screen-lg px-4 py-8 gap-8">
              {/* Filters Input */}
              <div className="rounded-full flex-0 shadow-lg pl-8 pr-4 py-4 flex items-center border gap-4 bg-white">
                <FiSearch style={{ color: textColor }} />
                <input
                  placeholder="Development Name, City, Address, Developer..."
                  className="bg-transparent flex-1 outline-none min-w-[70px]"
                  value={searchInputValue}
                  onChange={handleSearchInput}
                ></input>
                <div className="gap-2 items-center hidden min-[840px]:flex">
                  <SearchBarFilters
                    filters={filters}
                    mainColor={mainColor}
                    mainColorLight={mainColorLight}
                    textColor={textColor}
                    setFilters={setFilters}
                    goToFirstPage={() => setSkip(0)}
                  />
                </div>
                {renderFilterButton()}
              </div>

              {shouldRenderContent && (
                <>
                  {/* View Mode and quantity info */}
                  <div className="flex items-center gap-6 justify-between flex-col-reverse sm:flex-row">
                    {loadingProperties && <div />}
                    {!loadingProperties && selectedView === "GRID" && (
                      <div className="flex flex-col w-full sm:w-auto gap-2 ">
                        <p className="text-slate-400 text-sm text-center">
                          {resolveGridPropertyCountText()}
                        </p>
                        {isMapAreaRestricted && (
                          <button
                            onClick={() => {
                              clearLocationFilter();
                              setIsMapAreaRestricted(false);
                            }}
                            style={{
                              borderColor: mainColor,
                              color: mainColor,
                            }}
                            className={`py-1 px-2 rounded-md flex items-center justify-center gap-2 border hover:bg-gray-100 w-full sm:w-[200px]`}
                          >
                            <FiRefreshCcw size={14} />{" "}
                            <span className="text-sm">
                              Reset Map Boundaries
                            </span>
                          </button>
                        )}
                      </div>
                    )}
                    {!loadingProperties && selectedView === "MAP" && (
                      <p className="text-slate-400 text-sm">
                        {`${properties.length} Properties found`}
                      </p>
                    )}
                    <div className="flex gap-4 items-center">
                      <span className="">View Mode:</span>
                      <div className="flex items-center rounded-full border p-1 max-w-max">
                        <button
                          onClick={() => {
                            setSelectedView("GRID");
                            setPreviousState(null);
                          }}
                          style={{
                            backgroundColor:
                              selectedView === "GRID"
                                ? mainColor
                                : "transparent",
                            color:
                              selectedView === "GRID" ? "white" : textColor,
                          }}
                          className={`py-2 px-4 rounded-full flex items-center justify-center gap-2`}
                        >
                          <FiGrid /> <span className="text-sm">Grid</span>
                        </button>

                        <button
                          onClick={() => {
                            setSelectedView("MAP");
                            setIsMapAreaRestricted(true);
                            setPreviousState(null);
                          }}
                          style={{
                            backgroundColor:
                              selectedView === "MAP"
                                ? mainColor
                                : "transparent",
                            color: selectedView === "MAP" ? "white" : textColor,
                          }}
                          className={`py-2 px-4 rounded-full flex items-center justify-center gap-2`}
                        >
                          <FiMapPin /> <span className="text-sm">Map</span>
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Grid View */}
                  {selectedView === "GRID" && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {properties.map((property) => (
                        <Link
                          key={property._id}
                          className="rounded shadow-xl hover:scale-[1.02] transition-all relative bg-white"
                          style={{ color: textColor }}
                          to={`/exchange/embedded/${idxFeedId}/property/${property._id}`}
                          state={{
                            type: "GRID",
                            filters,
                            pagination: {
                              selectedPage,
                              limit,
                            },
                          }}
                        >
                          <img
                            src={
                              property.mainPropertyMediaFile?.files[0]
                                ?.publicUrl
                            }
                            className="rounded-t h-48 w-full object-cover"
                          />
                          {/* Relative tags */}
                          <div className="absolute top-4 left-4 flex gap-4">
                            {property.marketArea && (
                              <div
                                style={{ backgroundColor: mainColor }}
                                className="text-sm text-white rounded px-3 py-1"
                              >
                                <p>{property.marketArea.name}</p>
                              </div>
                            )}
                            {property.isMarkedAsVerified && (
                              <div
                                className="text-sm rounded px-3 py-1 shadow"
                                style={{
                                  backgroundColor: mainColorLight,
                                  color: mainColor,
                                }}
                              >
                                <p>Developer Verified</p>
                              </div>
                            )}
                          </div>
                          <div className="p-4 pb-6 flex flex-col">
                            <h2 className="text-lg font-bold">
                              {property.name}
                            </h2>
                            <div className="flex items-center mt-1 flex-wrap text-sm">
                              {property.priceRange && (
                                <>
                                  <p>
                                    $
                                    {property.priceRange?.min
                                      ? (
                                          property.priceRange?.min / 1000000
                                        ).toFixed(1)
                                      : 0}
                                    M - $
                                    {property.priceRange?.max
                                      ? (
                                          property.priceRange?.max / 1000000
                                        ).toFixed(1)
                                      : 0}
                                    M
                                  </p>
                                  <div
                                    className="h-5 w-0.5 mx-2"
                                    style={{ backgroundColor: textColorLight }}
                                  />
                                </>
                              )}
                              {property.averageBathroomsNumberValue && (
                                <>
                                  <p className="flex items-center gap-1">
                                    <Icon name="bathroom" size={24} />
                                    <span>
                                      {
                                        property.averageBathroomsNumberValue
                                          ?.min
                                      }
                                      -
                                      {
                                        property.averageBathroomsNumberValue
                                          ?.max
                                      }
                                    </span>
                                  </p>
                                </>
                              )}
                              {property.averageBedroomsNumberValue && (
                                <>
                                  <div
                                    className="h-5 w-0.5 mx-2"
                                    style={{ backgroundColor: textColorLight }}
                                  />
                                  <p className="flex items-center gap-1">
                                    <Icon name="bedroom" size={24} />
                                    <span>
                                      {property.averageBedroomsNumberValue?.min}
                                      -
                                      {property.averageBedroomsNumberValue?.max}
                                    </span>
                                  </p>
                                </>
                              )}
                            </div>
                            {property.projectStatus && (
                              <span
                                className="h-6 px-2 font-bold rounded text-xs flex items-center w-max capitalize mt-4 text-white"
                                style={{ backgroundColor: mainColor }}
                              >
                                {property.projectStatus
                                  ?.toLocaleLowerCase()
                                  .split("_")
                                  .join(" ")}
                              </span>
                            )}
                            <p className="flex items-center mt-4">
                              <FiMapPin />
                              <span className="ml-1 truncate text-sm">
                                {property.location?.address?.street},{" "}
                                {property.location?.address?.city},{" "}
                                {property.location?.address?.state},{" "}
                                {property.location?.address?.zipCode}
                              </span>
                            </p>
                            {property.yearBuilt && (
                              <p className="truncate mt-2 text-sm">
                                Completion in {property.yearBuilt}.
                              </p>
                            )}
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}

                  {/* Map View */}
                  {selectedView === "MAP" && (
                    <div className="relative">
                      <div className="h-[600px] w-full">
                        <ExchangeMapView
                          properties={properties as Property[]}
                          isLoadingProperties={loadingProperties}
                          idxFeedId={idxFeedId}
                          setLocationFilters={addLocationFilter}
                          branding={{ mainColor, textColor }}
                          previousMapState={previousMapState}
                          currentState={{
                            type: "MAP",
                            filters,
                            pagination: {
                              selectedPage,
                              limit,
                            },
                          }}
                        />
                      </div>
                      {loadingProperties && (
                        <div className="absolute bottom-4 left-0 z-50 w-full flex items-center justify-center">
                          <Spinner size={28} />
                        </div>
                      )}
                    </div>
                  )}

                  {/* Loading Feedback */}
                  {loadingProperties && selectedView === "GRID" && (
                    <div className="h-96 w-full flex items-center justify-center animate-pulse gap-2">
                      <FiLoader className="animate-spin text-sky-600" />
                      <p className="">Loading properties...</p>
                    </div>
                  )}

                  {/* Empty Feedback */}
                  {!loadingProperties &&
                    !properties.length &&
                    selectedView === "GRID" && (
                      <div className="h-96 w-full flex items-center justify-center gap-2">
                        <p className="text-center">
                          No properties found. Change your filters and try
                          again!
                        </p>
                      </div>
                    )}
                </>
              )}

              {/* Footer */}
              <footer className="flex flex-col-reverse sm:flex-row gap-8 items-center justify-between">
                <a
                  style={{ backgroundColor: mainColor, color: mainColor }}
                  className="flex items-center justify-center bg-sky-600 rounded-full py-2 px-4 flex-wrap gap-2 shrink-0 min-w-max"
                  href="https://dashtodash.com/"
                  target="_blank"
                >
                  <span className="text-sm text-white font-[montserrat]">
                    Powered By
                  </span>
                  <img src={DashLogo} alt="DASH" className="h-4" />
                </a>
                {/* Pagination */}
                {shouldRenderContent &&
                  !loadingProperties &&
                  selectedView === "GRID" && (
                    <div className="flex items-center justify-between">
                      <div className="flex gap-1">
                        <button
                          disabled={selectedPage === 1}
                          className="disabled:opacity-30 h-8 w-8 flex items-center justify-center rounded-lg border text-sm"
                          onClick={onPreviousPage}
                          style={{
                            color: mainColor,
                            borderColor: mainColor,
                          }}
                        >
                          <FiChevronLeft />
                        </button>
                        {pages.map((page) => {
                          return (
                            <button
                              key={page}
                              onClick={() => onGoToPage(page)}
                              style={{
                                backgroundColor:
                                  selectedPage === page
                                    ? mainColor
                                    : "transparent",
                                color:
                                  selectedPage === page ? "white" : mainColor,
                                borderColor:
                                  selectedPage === page
                                    ? "transparent"
                                    : mainColor,
                              }}
                              className={`h-8 w-8 flex items-center justify-center rounded-lg text-sm border`}
                            >
                              {page}
                            </button>
                          );
                        })}
                        <button
                          disabled={selectedPage === lastPage}
                          className="disabled:opacity-30 h-8 w-8 flex items-center justify-center rounded-lg border text-sm"
                          onClick={onNextPage}
                          style={{
                            color: mainColor,
                            borderColor: mainColor,
                          }}
                        >
                          <FiChevronRight />
                        </button>
                      </div>
                    </div>
                  )}
              </footer>
            </div>
          </div>
        </>
      )}
    </>
  );
}
